import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, UserCheck, Users, DollarSign, 
  CheckCircle, XCircle, Ban, Search, Clock, Wallet,
  FileText, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAffiliates = () => {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('affiliates');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [affiliatesRes, payoutsRes] = await Promise.all([
        fetch(`${API_URL}/api/affiliates/admin/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/affiliates/admin/payouts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (affiliatesRes.ok) {
        const data = await affiliatesRes.json();
        setAffiliates(data.affiliates || []);
      }
      
      if (payoutsRes.ok) {
        const data = await payoutsRes.json();
        setPayouts(data.payouts || []);
      }
    } catch (error) {
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (affiliateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/affiliates/admin/${affiliateId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Affiliate approved successfully');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to approve affiliate');
      }
    } catch (error) {
      toast.error('Failed to approve affiliate');
    }
  };

  const handleReject = async (affiliateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/affiliates/admin/${affiliateId}/reject`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Application rejected by admin' })
      });
      
      if (response.ok) {
        toast.success('Affiliate rejected');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to reject affiliate');
      }
    } catch (error) {
      toast.error('Failed to reject affiliate');
    }
  };

  const handleSuspend = async (affiliateId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/affiliates/admin/${affiliateId}/suspend`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Account suspended by admin' })
      });
      
      if (response.ok) {
        toast.success('Affiliate suspended');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to suspend affiliate');
      }
    } catch (error) {
      toast.error('Failed to suspend affiliate');
    }
  };

  const handleApprovePayout = async (payoutId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/affiliates/admin/payouts/${payoutId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Payout approved');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to approve payout');
      }
    } catch (error) {
      toast.error('Failed to approve payout');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      suspended: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      paid: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
    };
    return styles[status] || styles.pending;
  };

  const filteredAffiliates = affiliates.filter(a => 
    a.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.referral_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const pendingCount = affiliates.filter(a => a.status === 'pending').length;
  const activeCount = affiliates.filter(a => a.status === 'active').length;
  const totalCommissions = affiliates.reduce((sum, a) => sum + (a.balance || 0), 0);
  const pendingPayoutsCount = payouts.filter(p => p.status === 'pending').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="admin-affiliates">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/admin')}
            className="mb-4 border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Admin Dashboard
          </Button>
          
          <h1 className="font-heading text-3xl font-bold text-white">
            Affiliate <span className="text-cyan-400">Management</span>
          </h1>
          <p className="text-gray-400 mt-1">Manage affiliate applications and payouts</p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        >
          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Pending Applications</p>
                  <p className="text-2xl font-bold text-yellow-400 font-mono">{pendingCount}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Active Affiliates</p>
                  <p className="text-2xl font-bold text-green-400 font-mono">{activeCount}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Commissions</p>
                  <p className="text-2xl font-bold text-cyan-400 font-mono">${totalCommissions.toFixed(2)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-cyan-400/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(220,20%,7%)] border-white/10">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Pending Payouts</p>
                  <p className="text-2xl font-bold text-orange-400 font-mono">{pendingPayoutsCount}</p>
                </div>
                <Wallet className="w-8 h-8 text-orange-400/50" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-[hsl(220,20%,7%)] border border-white/10 p-1 mb-6">
              <TabsTrigger 
                value="affiliates"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500"
              >
                <Users className="w-4 h-4 mr-2" />
                Affiliates ({affiliates.length})
              </TabsTrigger>
              <TabsTrigger 
                value="payouts"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Payouts ({payouts.length})
              </TabsTrigger>
            </TabsList>

            {/* Affiliates Tab */}
            <TabsContent value="affiliates">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="Search by name, email, or referral code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-[hsl(220,20%,7%)] border-white/10 text-white"
                  />
                </div>
              </div>

              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-gray-400">Affiliate</TableHead>
                        <TableHead className="text-gray-400">Referral Code</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400">Balance</TableHead>
                        <TableHead className="text-gray-400">Referrals</TableHead>
                        <TableHead className="text-gray-400">Applied</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAffiliates.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            No affiliates found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredAffiliates.map((affiliate) => (
                          <TableRow key={affiliate.id} className="border-white/10 hover:bg-white/5">
                            <TableCell>
                              <div>
                                <p className="font-medium text-white">{affiliate.user_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{affiliate.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-cyan-400">{affiliate.referral_code}</span>
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(affiliate.status)}>
                                {affiliate.status?.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-white">${(affiliate.balance || 0).toFixed(2)}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-white">{affiliate.total_referrals || 0}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-gray-400 text-sm">
                                {new Date(affiliate.created_at).toLocaleDateString()}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-2 justify-end">
                                {affiliate.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleApprove(affiliate.id)}
                                      className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleReject(affiliate.id)}
                                      className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {affiliate.status === 'active' && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleSuspend(affiliate.id)}
                                    className="bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 border border-gray-500/30"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts">
              <Card className="bg-[hsl(220,20%,7%)] border-white/10">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-gray-400">Affiliate</TableHead>
                        <TableHead className="text-gray-400">Amount</TableHead>
                        <TableHead className="text-gray-400">Method</TableHead>
                        <TableHead className="text-gray-400">Status</TableHead>
                        <TableHead className="text-gray-400">Requested</TableHead>
                        <TableHead className="text-gray-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payouts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                            No payout requests found
                          </TableCell>
                        </TableRow>
                      ) : (
                        payouts.map((payout) => (
                          <TableRow key={payout.id} className="border-white/10 hover:bg-white/5">
                            <TableCell>
                              <div>
                                <p className="font-medium text-white">{payout.affiliate_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{payout.affiliate_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono font-bold text-green-400">${payout.amount?.toFixed(2)}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-white capitalize">{payout.payment_method?.replace('_', ' ')}</span>
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusBadge(payout.status)}>
                                {payout.status?.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-gray-400 text-sm">
                                {new Date(payout.created_at).toLocaleDateString()}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {payout.status === 'pending' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleApprovePayout(payout.id)}
                                  className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                              {payout.status === 'approved' && (
                                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                                  Processing
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAffiliates;
